// Layouts
import DefaultLayout from "../layouts/default";
import { DocNavProvider } from "./documentation/DocNavContext";
// Components
import React, {useEffect, useRef, useState, createContext, useContext} from "react";
import ThreePhasesSection from "../components/sections/ThreePhases";
import BackToTop from "../components/base/BackToTop";
import HeaderBanner from "../components/sections/HeaderBanner";
import {Outlet, useLocation} from "react-router-dom";
import { useDocNav } from "./documentation/DocNavContext";
import BaseButton from "../components/base/Button";
import DocumentationNav from "./documentation/DocumentationNav";

// Safety Popup Context
type SafetyPopupContextType = {
  safetyTermsPopup: boolean;
  setSafetyTermsPopup: (value: boolean) => void;
  safetyCheck: boolean;
  setSafetyCheck: (value: boolean) => void;
  showTermsOfService: boolean;
  setShowTermsOfService: (value: boolean) => void;
};

const SafetyPopupContext = createContext<SafetyPopupContextType | undefined>(undefined);

export const useSafetyPopup = () => {
  const context = useContext(SafetyPopupContext);
  if (context === undefined) {
    throw new Error('useSafetyPopup must be used within a SafetyPopupProvider');
  }
  return context;
};

export const SafetyPopupProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [safetyCheck, setSafetyCheck] = useState(localStorage.getItem('safetyTermsInjections') === "true");
  const [safetyTermsPopup, setSafetyTermsPopup] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(true);

  const value = {
    safetyTermsPopup,
    setSafetyTermsPopup,
    safetyCheck,
    setSafetyCheck,
    showTermsOfService,
    setShowTermsOfService
  };

  return (
    <SafetyPopupContext.Provider value={value}>
      {children}
      {safetyTermsPopup && <SafetyPopup />}
    </SafetyPopupContext.Provider>
  );
};

// Safety Popup Component
const SafetyPopup = () => {
  const { setSafetyTermsPopup, setSafetyCheck, setShowTermsOfService } = useSafetyPopup();
  
  const closeSafetyPopup = () => {
    setSafetyTermsPopup(false);
    document.documentElement.classList.remove('overflow-hidden');
  };

  useEffect(() => {
    document.documentElement.classList.add('overflow-hidden');
    return () => {
      document.documentElement.classList.remove('overflow-hidden');
    };
  }, []);

  return (
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 z-[9999] flex items-center justify-center w-full h-full bg-black bg-opacity-90 overflow-y-auto overflow-x-hidden"
    >
      <div className="relative p-4 w-full max-w-4xl max-h-full m-auto">
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-900">
          <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
            <span className="text-xl font-semibold text-gray-900 dark:text-white">
              Terms of Service
            </span>
            <button 
              type="button" 
              onClick={closeSafetyPopup} 
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white" 
              data-modal-hide="default-modal"
            >
              <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          <div className="p-4 md:p-5 space-y-4">
            <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">Before accessing the "Prompt Injections" examples on our website, please read and agree to the following terms of service:</p>
            <ul className="px-10 list-decimal leading-relaxed text-gray-500 dark:text-gray-400">
              <li className="pb-4"><strong>Educational Purpose Only</strong>: The "Prompt Injections" examples provided are intended solely for educational purposes. They are meant to help you understand how prompt injections work and how to defend yourself against them.</li>
              <li className="pb-4"><strong>No Misuse</strong>: You agree not to use the provided examples for any malicious or unethical activities. This includes, but is not limited to, using prompt injections to manipulate, deceive, or harm others.</li>
              <li className="pb-4"><strong>Responsible Use</strong>: By accessing these examples, you confirm that your intention is to learn about the risks associated with prompt injections and to enhance your ability to safeguard against them.</li>
              <li className="pb-4"><strong>Legal Compliance</strong>: You agree to comply with all applicable laws and regulations while using the information provided on this website.</li>
              <li className="pb-4"><strong>No Liability</strong>: We are not responsible for any misuse of the information provided on our website. Users are solely responsible for their actions and any consequences that may arise from the use of this information.</li>
            </ul>

            <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">By clicking "Agree," you acknowledge and accept the terms of service. By clicking "Agree & Save," you accept the terms and save your choice for future visits. By clicking "Decline," you choose not to accept the terms and cannot access this section.</p>
          </div>
          <div className="flex items-center p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600">
            <button data-modal-hide="default-modal" onClick={() => { setSafetyCheck(true); closeSafetyPopup(); }} type="button" className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 mr-3">I agree</button>
            <button data-modal-hide="default-modal" onClick={() => { setSafetyCheck(true); localStorage.setItem('safetyTermsInjections', "true"); closeSafetyPopup(); }} type="button" className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">I agree & save</button>
            <button data-modal-hide="default-modal" onClick={() => { setShowTermsOfService(false); closeSafetyPopup(); }} type="button" className="py-2.5 px-5 ms-3 text-sm font-medium text-white focus:outline-none bg-red-600 rounded-lg border border-gray-200 hover:bg-red-900 hover:text-gray-200 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-red-600 dark:text-white dark:border-gray-600 dark:hover:text-white dark:hover:bg-red-900">Decline</button>
          </div>
        </div>
      </div>
    </div>
  );
};

function DocumentationInner() {
    const location = useLocation();
    const lastHash = useRef('');
    const [currentSection, setCurrentSection] = React.useState<string>("");

    // Existing hash navigation logic...
    React.useEffect(() => {
        goToLocation(-20);
    }, [location.hash]);

    function goToLocation(yOffset: number) {
        if (location.hash) {
            lastHash.current = location.hash.slice(1);
        }
        if (lastHash.current && document.getElementById(lastHash.current)) {
            setTimeout(() => {
                const element = document.getElementById(lastHash.current);
                if (element) {
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
                lastHash.current = '';
            }, 50);
        }
    }

    React.useEffect(() => {
        const headings = Array.from(document.querySelectorAll("h2[id],h3[id]"));
        const handleScroll = () => {
            let current = "";
            for (const heading of headings) {
                const rect = heading.getBoundingClientRect();
                if (rect.top <= 80) {
                    current = heading.id;
                }
            }
            setCurrentSection(current);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, [location.pathname]); // re-run if page changes

  // Get page and id from route state or params (fallbacks for backward compatibility)
  const routeState = location.state || {};
  // Get docPageId from DocNavContext (set by child doc pages)
  const { docPageId } = useDocNav();
  const page = docPageId || routeState.page || (location.pathname.split('/').pop()?.replace(/-/g, '') || '');
  const sectionId = routeState.sectionId || (location.pathname.split('/').pop()?.toLowerCase() || 'documentation');

  return (
    <div className="Framework">
      <DefaultLayout>
        <HeaderBanner title="Framework"
            buttons={[
                { title: "IPE Framework", url: "/documentation/framework#documentation" },
                { title: "Techniques", url: "/documentation/prompt-techniques#prompt-techniques" },
                { title: "Adversarial Prompts", url: "/documentation/adversarial-prompting#adversarial-prompting" },
            ]}
        />
      {/* Shared documentation layout */}
      <section className="w-full pb-24 flex dark:bg-slate-900 dark:text-white" id={sectionId}>
        <div className="relative max-w-screen-xl px-4 sm:px-8 mx-auto grid grid-cols-12 gap-x-6">
          {/* Sidebar navigation */}
          <DocumentationNav page={page} currentSection={currentSection} />

          {/* Main content area for each doc page */}
          <div className="col-span-12 lg:col-span-9 space-y-6 px-4 sm:px-6 mt-20 flex-1 dark:text-white content-section"
               data-aos="fade-up"
               data-aos-delay="100">
            <Outlet context={{ currentSection }} />
          </div>
        </div>
      </section>

      {/* Mobile navigation and extras (unchanged) */}
      <div className="lg:hidden">
          <section className="relative max-w-full overflow-hidden">
              <div className="w-full py-5 flex flex-col items-center">
                  <h1
                      data-aos="fade-right"
                      data-aos-once="true"
                      className="text-[2.5rem] sm:text-5xl xl:text-6xl font-bold leading-tight capitalize pb-5 dark:text-white"
                  >
                      <span className="text-header-gradient">Read more</span>
                  </h1>
                  <div
                      data-aos="fade-up"
                      data-aos-once="true"
                      data-aos-delay="700"
                      className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mt-2"
                  >
                    {
                        location.pathname === "/documentation/prompt-engineering" ? "" :
                            <BaseButton url="/documentation/prompt-engineering" styles="max-w-full px-8 py-4 border border-[#0c66ee] dark:text-white">Prompt Engineering</BaseButton>
                    }
                    {
                        location.pathname === "/documentation/framework" ? "" :
                            <BaseButton url="/documentation/framework" styles="max-w-full px-8 py-4 border border-[#0c66ee] dark:text-white">Framework</BaseButton>
                    }
                    {
                        location.pathname === "/documentation/framework/visual" ? "" :
                            <BaseButton url="/documentation/framework/visual" styles="max-w-full px-8 py-4 border border-[#0c66ee] dark:text-white">Framework IVPE</BaseButton>
                    }
                  </div>
              </div>
          </section>
      </div>

      <ThreePhasesSection />
      <BackToTop sticky={true} />
    </DefaultLayout>
  </div>
  );
}

export default function Documentation() {
  return (
    <DocNavProvider>
      <SafetyPopupProvider>
        <DocumentationInner />
      </SafetyPopupProvider>
    </DocNavProvider>
  );
}
